import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import useApi from "../../../core/customhook/useApi";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";

dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);

interface Holiday {
  holiday: string;
  holiday_date: string;
  holiday_category: string;
  location: string;
}

interface HolidaysMainResponse {
  success: boolean;
  message: string;
  statusCode: number;
  data: Holiday[];
}

const OutdutyCalender = () => {
  const [events, setEvents] = useState<any>([]);
  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());
  const [rhHolidaySet, setRhHolidaySet] = useState<Set<string>>(new Set());
  const [weekendSet, setWeekendSet] = useState<Set<string>>(new Set());

  const formatFinancialYear = (year: number) => {
    const nextYearShort = (year + 1).toString().slice(-2);
    return `${year}-${nextYearShort}`;
  };

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    return formatFinancialYear(fyStart);
  };

  const [finance_year, setFinance_year] = useState(getCurrentFinancialYear());

  const {
    data: holidayResponse,
    loading: holidayLoading,
    error: holidayError,
  } = useApi<HolidaysMainResponse>("/api/holiday-list", {
    method: "POST",
    autoCall: true,
    payload: {
      category: "",
    },
  });

  const {
    data: weekendResponse,
    loading: weekendLoading,
    error: weekendError,
  } = useApi("/api/weekend-rule", {
    method: "GET",
    autoCall: true,
  });

  const {
    data: od_details,
    loading: od_details_loading,
    error: od_details_error,
  } = useApi("/api/view-od-status", {
    method: "POST",
    autoCall: true,
    payload: {
      financial_year: finance_year,
    },
  });

  type LeaveStatus = "APPROVED" | "PENDING" | "REJECTED";

  const statusColors: Record<
    LeaveStatus,
    { backgroundColor: string; borderColor: string; textColor?: string }
  > = {
    APPROVED: {
      backgroundColor: "green",
      borderColor: "green",
      textColor: "#fff",
    },
    PENDING: {
      backgroundColor: "yellow",
      borderColor: "#ccc",
      textColor: "#000",
    },
    REJECTED: {
      backgroundColor: "red",
      borderColor: "red",
      textColor: "#fff",
    },
  };

  useEffect(() => {
    if (holidayResponse?.data) {
      const holSet = new Set(holidayResponse.data.map((h) => h.holiday_date));

      const rhSet = new Set(
        holidayResponse.data
          .filter((h) => h.holiday_category === "RH")
          .map((h) => h.holiday_date)
      );

      const holidayEvents = holidayResponse.data.map((h) => {
        let textColor = "#dc3545"; // GH default: red
        if (h.holiday_category === "RH") {
          textColor = "#007bff"; // RH: blue
        } else if (h.holiday_category === "MH") {
          textColor = "red";
        }

        return {
          id: `hol-${h.holiday_date}`,
          title: h.holiday,
          start: h.holiday_date,
          allDay: true,
          backgroundColor: "transparent",
          textColor: textColor,
          extendedProps: {
            category: h.holiday_category,
            isHoliday: true,
          },
        };
      });

      // Weekend logic — match leave calendar
      const startOfYear = dayjs(`${finance_year.slice(0, 4)}-04-01`);
      const endOfYear = startOfYear.add(1, "year").subtract(1, "day");

      // Last Saturdays of each month
      const lastSaturdays = new Set<string>();
      let monthCursor = startOfYear.startOf("month");
      while (monthCursor.isBefore(endOfYear)) {
        let lastDayOfMonth = monthCursor.endOf("month");
        while (lastDayOfMonth.day() !== 6) {
          lastDayOfMonth = lastDayOfMonth.subtract(1, "day");
        }
        lastSaturdays.add(lastDayOfMonth.format("YYYY-MM-DD"));
        monthCursor = monthCursor.add(1, "month");
      }

      const weekendDates: string[] = [];
      let current = startOfYear;
      while (current.isSameOrBefore(endOfYear, "day")) {
        const dateStr = current.format("YYYY-MM-DD");
        const day = current.day(); // 0 = Sunday, 6 = Saturday

        if (day === 0 || (day === 6 && !lastSaturdays.has(dateStr))) {
          weekendDates.push(dateStr);
        }

        current = current.add(1, "day");
      }

      const weekendEvents = weekendDates.map((date) => ({
        id: `wd-${date}`,
        title: "",
        start: date,
        allDay: true,
      }));

      setHolidaySet(holSet);
      setRhHolidaySet(rhSet);
      setWeekendSet(new Set(weekendDates));
      setEvents((prev: any) => [...holidayEvents, ...weekendEvents, ...prev]);
    }
  }, [holidayResponse, weekendResponse, finance_year]);
  useEffect(() => {
    if (od_details?.data && holidayResponse?.data) {
      const odEvents: any[] = [];

      const filteredod = od_details.data.filter(
        (item: any) => item.status?.toUpperCase() !== "REJECTED"
      );

      filteredod.forEach((l: any) => {
        const {
          from_date,
          to_date,
          od_type,
          status,
          od_application_id,
          application_reason,
          od_slice_type,
        } = l;

        const startDate = dayjs(from_date);
        const endDate = to_date ? dayjs(to_date) : null;
        const isHalfDay = od_slice_type?.toLowerCase().includes("half");

        if (isHalfDay) {
          const date = startDate.format("YYYY-MM-DD");

          const holidayObj = holidayResponse.data.find(
            (h: any) => h.holiday_date === date
          );
          const isMHOrGH = holidayObj && holidayObj.holiday_category !== "RH";

          if (!isMHOrGH && !weekendSet.has(date)) {
            odEvents.push({
              id: `od-${od_application_id}-${date}`,
              title: `${od_type} (${od_slice_type})`,
              start: date,
              allDay: true,
              ...statusColors[status as LeaveStatus],
              extendedProps: { application_reason, status },
            });
          }
        } else if (endDate) {
          let curr = startDate;
          while (curr.isSameOrBefore(endDate, "day")) {
            const date = curr.format("YYYY-MM-DD");

            const holidayObj = holidayResponse.data.find(
              (h: any) => h.holiday_date === date
            );
            const isMHOrGH = holidayObj && holidayObj.holiday_category !== "RH";

            if (!isMHOrGH && !weekendSet.has(date)) {
              odEvents.push({
                id: `od-${od_application_id}-${date}`,
                title: od_type,
                start: date,
                allDay: true,
                ...statusColors[status as LeaveStatus],
                extendedProps: { application_reason, status },
              });
            }
            curr = curr.add(1, "day");
          }
        }
      });

      setEvents((prev: any) => [
        ...prev.filter((e: any) => !e.id.startsWith("od-")),
        ...odEvents,
      ]);
    }
  }, [od_details, holidayResponse, weekendSet]);

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Out Duty Calendar</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Work & Operations</li>
                  <li className="breadcrumb-item">OD</li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.selfOutDutyGrid}>Self Out Duty</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Out Duty Calendar
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 col-xxl-12 col-xl-12  theiaStickySidebar">
              <div className="stickybar">
                <div className="card border-0">
                  <div className="card-body">
                    <FullCalendar
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth"
                      headerToolbar={{ start: "today", center: "title", end: "prev,next" }}
                      height="auto"
                      events={events}
                      dayCellContent={({ date }) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");

                        // Match event by date
                        const matchedEvent = events.find((e: any) => e.start === formattedDate);

                        let dotColor = "";
                        let tooltip = "";

                        if (matchedEvent) {
                          const title = matchedEvent.title || "";
                          const status = matchedEvent.extendedProps?.status;
                          const isHoliday = holidaySet.has(formattedDate);
                          const isWeekend = weekendSet.has(formattedDate);

                          if (isHoliday) {
                            const holidayDetails = holidayResponse?.data.find(h => h.holiday_date === formattedDate);
                            const category = holidayDetails?.holiday_category;
                            dotColor = category === "RH" ? "#007bff" : "red"; // Blue for RH, Red for GH
                            tooltip = `${holidayDetails?.holiday} (${category})`;
                          } else if (isWeekend) {
                            dotColor = "#17a2b8"; // Blue-ish for weekends
                            tooltip = "Weekend";
                          } else if (status) {
                            switch (status.toUpperCase()) {
                              case "APPROVED":
                                dotColor = "#28a745";
                                tooltip = "Approved";
                                break;
                              case "PENDING":
                                dotColor = "#fd7e14";
                                tooltip = "Pending";
                                break;
                              case "REJECTED":
                                dotColor = "#dc3545";
                                tooltip = "Rejected";
                                break;
                              default:
                                dotColor = "#6c757d";
                                tooltip = status;
                            }
                          }
                        }

                        return (
                          <div style={{ position: "relative", textAlign: "center" }}>
                            <div>{date.getDate()}</div>
                            {dotColor && (
                              <div style={{ display: "flex", justifyContent: "center", marginTop: "5px" }}>
                                <span
                                  title={tooltip}
                                  style={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: dotColor,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                    {/* Legend */}
                    <div className="py-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="row">
                            <div className="">
                              <ul className="list-unstyled mb-2 d-flex gap-5">
                                <li>
                                  <strong
                                    style={{
                                      display: "inline-block",
                                      width: "16px",
                                      height: "16px",
                                      backgroundColor: "green",
                                      marginRight: "8px",
                                    }}
                                  ></strong>
                                  Approved
                                </li>
                                <li>
                                  <strong
                                    style={{
                                      display: "inline-block",
                                      width: "16px",
                                      height: "16px",
                                      backgroundColor: "#FFFF00",
                                      marginRight: "8px",
                                      border: "1px solid #ccc",
                                    }}
                                  ></strong>
                                  Pending
                                </li>
                                <li>
                                  <strong
                                    style={{
                                      display: "inline-block",
                                      width: "16px",
                                      height: "16px",
                                      backgroundColor: "red",
                                      marginRight: "8px",
                                    }}
                                  ></strong>
                                  Holiday
                                </li>
                                <li>
                                  <strong
                                    style={{
                                      display: "inline-block",
                                      width: "16px",
                                      height: "16px",
                                      backgroundColor: "#ADD8E6",
                                      marginRight: "8px",
                                    }}
                                  ></strong>
                                  Weekend
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Legend */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default OutdutyCalender;
